import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";

import { ApiError } from "../lib/api";
import { useAvisoSalida } from "../lib/useAvisoSalida";
import { Modal } from "./Modal";
import { useToast } from "./Toast";
import { Button } from "./ui";

export interface FilaImportacion {
  fila: number;
  clave: string;
  motivo: string;
}

/** Lo común a los importadores del backend (insumos, activos). */
export interface ResultadoImportacion {
  confirmado: boolean;
  filas: number;
  creados: number;
  actualizados: number;
  sin_cambio: number;
  errores: FilaImportacion[];
  avisos?: FilaImportacion[];
}

/**
 * Subir una planilla en dos pasos: **revisar** (vista previa, el backend no
 * guarda nada) y **confirmar**. Las filas con error no entran; el resto sí.
 */
export function ImportarPlanillaModal<R extends ResultadoImportacion>({
  titulo,
  descripcion,
  importar,
  descargarPlantilla,
  detalle,
  onClose,
  onImportado,
}: {
  titulo: string;
  descripcion: React.ReactNode;
  importar: (archivo: File, confirmar: boolean) => Promise<R>;
  descargarPlantilla: () => Promise<void>;
  /** Líneas extra del resumen (stock cargado, categorías nuevas…). */
  detalle?: (r: R) => string[];
  onClose: () => void;
  onImportado: (r: R) => void;
}): JSX.Element {
  const toast = useToast();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<R | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: ({ confirmar }: { confirmar: boolean }) => importar(archivo as File, confirmar),
    onSuccess: (r) => {
      if (r.confirmado) {
        toast.success(`Importación lista: ${r.creados} nuevos, ${r.actualizados} actualizados`);
        onImportado(r);
        onClose();
      } else {
        setPrevia(r);
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "No se pudo leer el archivo");
    },
  });

  const aplicables = previa ? previa.creados + previa.actualizados : 0;
  // Una planilla grande tarda: mientras el servidor trabaja, el modal no se
  // cierra y el navegador avisa antes de recargar o cerrar la pestaña.
  const ocupado = mut.isPending;
  useAvisoSalida(ocupado);
  const cerrar = ocupado ? () => undefined : onClose;

  return (
    <Modal
      open
      onClose={cerrar}
      title={titulo}
      icon={FileSpreadsheet}
      size="lg"
      closeOnBackdrop={!mut.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          {previa === null ? (
            <Button
              icon={Upload}
              onClick={() => {
                setError(null);
                mut.mutate({ confirmar: false });
              }}
              disabled={archivo === null || mut.isPending}
            >
              {mut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Revisando…
                </>
              ) : (
                "Revisar archivo"
              )}
            </Button>
          ) : (
            <Button
              icon={CheckCircle2}
              onClick={() => mut.mutate({ confirmar: true })}
              disabled={aplicables === 0 || mut.isPending}
            >
              {mut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Importando {aplicables} filas…
                </>
              ) : (
                `Confirmar (${aplicables})`
              )}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="text-neutral-600">{descripcion}</div>

        <Button
          variant="link"
          size="sm"
          icon={Download}
          onClick={() => {
            descargarPlantilla().catch((e: unknown) =>
              toast.error(e instanceof ApiError ? e.message : "No se pudo descargar"),
            );
          }}
        >
          Descargar planilla modelo
        </Button>

        <label className="block">
          <span className="mb-1 block font-medium text-neutral-700">Archivo (.xlsx o .csv)</span>
          <input
            type="file"
            accept=".xlsx,.xlsm,.csv,.txt"
            onChange={(e) => {
              setArchivo(e.target.files?.[0] ?? null);
              setPrevia(null);
              setError(null);
            }}
            disabled={mut.isPending}
            className="block w-full text-sm text-neutral-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-800 hover:file:bg-brand-200"
          />
        </label>

        {ocupado && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <span>
              Con muchas filas esto puede tardar unos minutos. No cierres ni recargues la página.
            </span>
          </p>
        )}

        {error && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700">
            {error}
          </p>
        )}

        {previa && (
          <div className="space-y-3">
            <p className="font-medium text-neutral-800">
              Vista previa de {previa.filas} filas. Todavía no se guardó nada.
            </p>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Cifra label="Nuevos" valor={previa.creados} />
              <Cifra label="Actualizados" valor={previa.actualizados} />
              <Cifra label="Sin cambios" valor={previa.sin_cambio} />
              <Cifra label="Con error" valor={previa.errores.length} alerta />
            </dl>
            {detalle && detalle(previa).length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-neutral-600">
                {detalle(previa).map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
            <ListaFilas titulo="Filas que no se importarán" filas={previa.errores} tono="error" />
            <ListaFilas titulo="Avisos" filas={previa.avisos ?? []} tono="aviso" />
          </div>
        )}
      </div>
    </Modal>
  );
}

function Cifra({ label, valor, alerta }: { label: string; valor: number; alerta?: boolean }): JSX.Element {
  const rojo = alerta === true && valor > 0;
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${rojo ? "border-rose-200 bg-rose-50" : "border-neutral-200 bg-neutral-50"}`}
    >
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className={`text-lg font-semibold tabular-nums ${rojo ? "text-rose-700" : "text-neutral-900"}`}>
        {valor}
      </dd>
    </div>
  );
}

function ListaFilas({
  titulo,
  filas,
  tono,
}: {
  titulo: string;
  filas: FilaImportacion[];
  tono: "error" | "aviso";
}): JSX.Element | null {
  if (filas.length === 0) return null;
  const color = tono === "error" ? "text-rose-700" : "text-amber-700";
  return (
    <div>
      <p className={`mb-1 flex items-center gap-1.5 font-medium ${color}`}>
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {titulo} ({filas.length})
      </p>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-neutral-200">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-neutral-50 text-neutral-500">
            <tr>
              <th scope="col" className="px-2 py-1 text-left">Fila</th>
              <th scope="col" className="px-2 py-1 text-left">Código</th>
              <th scope="col" className="px-2 py-1 text-left">Motivo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {filas.map((f) => (
              <tr key={`${f.fila}-${f.motivo}`}>
                <td className="px-2 py-1 tabular-nums text-neutral-600">{f.fila}</td>
                <td className="px-2 py-1 font-mono text-neutral-700">{f.clave || "—"}</td>
                <td className="px-2 py-1 text-neutral-700">{f.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
